/**
 * Calculates the unfolded (flat) dimensions required to achieve a desired finished size after folding.
 * Uses consistent fold definitions:
 * - Half fold: Always folds through the LONG side of the unfolded sheet
 * - Parallel fold: Always folds through the SHORT side of the unfolded sheet
 *
 * @param {number} finishedWidth_mm - The width of the finished (folded) item in mm.
 * @param {number} finishedHeight_mm - The height of the finished (folded) item in mm.
 * @param {string} foldType - The type of fold (e.g., 'half', 'tri', 'z', 'gate', 'cross', 'accordion', 'parallel').
 * @returns {object} An object containing:
 *  - unfolded_width_mm {number}: The width of the flat sheet.
 *  - unfolded_height_mm {number}: The height of the flat sheet.
 *  - panels {number}: The number of panels on one side of the finished item.
 *  - fold_orientation {string|null}: 'vertical', 'horizontal', or 'both', indicating the primary fold direction(s) relative to the finished item.
 */
export const calculateUnfoldedDimensions = (finishedWidth_mm, finishedHeight_mm, foldType) => {
  if (!foldType || finishedWidth_mm <= 0 || finishedHeight_mm <= 0) {
    return {
      unfoldedWidthMm: finishedWidth_mm,
      unfoldedHeightMm: finishedHeight_mm,
      panels: 1,
      foldOrientation: null
    };
  }

  let unfoldedWidthMm = parseFloat(finishedWidth_mm);
  let unfoldedHeightMm = parseFloat(finishedHeight_mm);
  let panels = 1;
  let foldOrientation = "vertical"; // Default

  switch (foldType) {
    case 'half':
      // CHANGED: Half fold always folds through the LONG side of the unfolded sheet
      // This means we double the SHORTER dimension of the finished item
      if (finishedWidth_mm <= finishedHeight_mm) {
        // Finished is portrait/square, so unfolded should be landscape (double width)
        unfoldedWidthMm = finishedWidth_mm * 2;
        unfoldedHeightMm = finishedHeight_mm;
        foldOrientation = "vertical"; // Fold line is vertical (parallel to height)
      } else {
        // Finished is landscape, so unfolded should be portrait (double height)
        unfoldedWidthMm = finishedWidth_mm;
        unfoldedHeightMm = finishedHeight_mm * 2;
        foldOrientation = "horizontal"; // Fold line is horizontal (parallel to width)
      }
      panels = 2;
      break;

    case 'parallel':
      // CHANGED: Parallel fold always folds through the SHORT side of the unfolded sheet
      // This means we double the LONGER dimension of the finished item
      if (finishedWidth_mm >= finishedHeight_mm) {
        // Finished is landscape/square, so unfolded should be portrait (double height)
        unfoldedWidthMm = finishedWidth_mm;
        unfoldedHeightMm = finishedHeight_mm * 2;
        foldOrientation = "horizontal"; // Fold line is horizontal
      } else {
        // Finished is portrait, so unfolded should be landscape (double width)
        unfoldedWidthMm = finishedWidth_mm * 2;
        unfoldedHeightMm = finishedHeight_mm;
        foldOrientation = "vertical"; // Fold line is vertical
      }
      panels = 2;
      break;

    case 'tri':
    case 'z':
      // Tri-fold always along the longest dimension of the unfolded sheet
      // So we triple the shorter dimension of the finished item
      if (finishedWidth_mm <= finishedHeight_mm) {
        unfoldedWidthMm = finishedWidth_mm * 3;
        unfoldedHeightMm = finishedHeight_mm;
        foldOrientation = "vertical";
      } else {
        unfoldedWidthMm = finishedWidth_mm;
        unfoldedHeightMm = finishedHeight_mm * 3;
        foldOrientation = "horizontal";
      }
      panels = 3;
      break;

    case 'gate':
      // Gate fold along the longest dimension
      if (finishedWidth_mm <= finishedHeight_mm) {
        unfoldedWidthMm = finishedWidth_mm * 2;
        unfoldedHeightMm = finishedHeight_mm;
        foldOrientation = "vertical";
      } else {
        unfoldedWidthMm = finishedWidth_mm;
        unfoldedHeightMm = finishedHeight_mm * 2;
        foldOrientation = "horizontal";
      }
      panels = 3;
      break;

    case 'cross':
      unfoldedWidthMm = finishedWidth_mm * 2;
      unfoldedHeightMm = finishedHeight_mm * 2;
      panels = 4;
      foldOrientation = "both";
      break;

    case 'accordion':
      // Accordion along the longest dimension
      if (finishedWidth_mm <= finishedHeight_mm) {
        unfoldedWidthMm = finishedWidth_mm * 4;
        unfoldedHeightMm = finishedHeight_mm;
        foldOrientation = "vertical";
      } else {
        unfoldedWidthMm = finishedWidth_mm;
        unfoldedHeightMm = finishedHeight_mm * 4;
        foldOrientation = "horizontal";
      }
      panels = 4;
      break;

    default:
      console.warn(`Unknown fold type '${foldType}' in calculateUnfoldedDimensions. Returning original dimensions.`);
      break;
  }

  return { unfoldedWidthMm, unfoldedHeightMm, panels, foldOrientation };
};

/**
 * Calculates the finished (folded) dimensions that result from folding a flat sheet.
 * Uses consistent fold definitions:
 * - Half fold: Always folds through the LONG side
 * - Parallel fold: Always folds through the SHORT side
 *
 * @param {number} unfoldedWidth_mm - The width of the flat (unfolded) sheet in mm.
 * @param {number} unfoldedHeight_mm - The height of the flat (unfolded) sheet in mm.
 * @param {string} foldType - The type of fold to apply.
 * @returns {object} An object containing:
 *  - width {number}: The width of the folded item.
 *  - height {number}: The height of the folded item.
 */
export const calculateFoldedDimensions = (unfoldedWidth_mm, unfoldedHeight_mm, foldType) => {
  if (!foldType || unfoldedWidth_mm <= 0 || unfoldedHeight_mm <= 0) {
    return { width: unfoldedWidth_mm, height: unfoldedHeight_mm };
  }

  let foldedWidth = parseFloat(unfoldedWidth_mm);
  let foldedHeight = parseFloat(unfoldedHeight_mm);

  switch (foldType) {
    case 'half':
      // CHANGED: Half fold always folds through the LONG side
      if (unfoldedWidth_mm >= unfoldedHeight_mm) {
        // Unfolded is landscape, fold through the long side (width)
        foldedWidth = unfoldedWidth_mm / 2;
        foldedHeight = unfoldedHeight_mm;
      } else {
        // Unfolded is portrait, fold through the long side (height)
        foldedWidth = unfoldedWidth_mm;
        foldedHeight = unfoldedHeight_mm / 2;
      }
      break;

    case 'parallel':
      // CHANGED: Parallel fold always folds through the SHORT side
      if (unfoldedWidth_mm <= unfoldedHeight_mm) {
        // Unfolded is portrait, fold through the short side (width)
        foldedWidth = unfoldedWidth_mm / 2;
        foldedHeight = unfoldedHeight_mm;
      } else {
        // Unfolded is landscape, fold through the short side (height)
        foldedWidth = unfoldedWidth_mm;
        foldedHeight = unfoldedHeight_mm / 2;
      }
      break;

    case 'tri':
    case 'z':
      // Tri-fold along the longest dimension
      if (unfoldedWidth_mm >= unfoldedHeight_mm) {
        foldedWidth = unfoldedWidth_mm / 3;
      } else {
        foldedHeight = unfoldedHeight_mm / 3;
      }
      break;

    case 'gate':
      // Gate fold along the longest dimension
      if (unfoldedWidth_mm >= unfoldedHeight_mm) {
        foldedWidth = unfoldedWidth_mm / 2;
      } else {
        foldedHeight = unfoldedHeight_mm / 2;
      }
      break;

    case 'cross':
      foldedWidth = unfoldedWidth_mm / 2;
      foldedHeight = unfoldedHeight_mm / 2;
      break;

    case 'accordion':
      // Accordion along the longest dimension
      if (unfoldedWidth_mm >= unfoldedHeight_mm) {
        foldedWidth = unfoldedWidth_mm / 4;
      } else {
        foldedHeight = unfoldedHeight_mm / 4;
      }
      break;

    default:
      console.warn(`Unknown fold type '${foldType}' in calculateFoldedDimensions. Returning original dimensions.`);
      break;
  }

  return { width: foldedWidth, height: foldedHeight };
};

// Helper to format dimensions for display
export const formatDimensions = (w, h, unit = 'mm') => {
  const round = (num) => Math.round(num * 10) / 10; // Round to 1 decimal place
  return `${round(w)}${unit} × ${round(h)}${unit}`;
};